import * as z from 'zod';

/**
 * Zod schemas for the rich Courier-Detail domain (`courier-profile`). This is a
 * DISTINCT slice from the list-row `courier` entity: `courier` is the broad
 * couriers-table row, whereas `courier-profile` is the full-fidelity detail
 * payload the Courier Detail screen (summary card + 5 tabs) reads.
 *
 * Zod is the single source of truth — every type is inferred, never
 * hand-written (CLAUDE.md). Each api call parses its response against one of
 * these schemas so backend contract drift fails loudly at the boundary.
 */

/** Tolerant id — the API sends numeric ids; the UI stringifies for routing. */
export const courierProfileIdSchema = z.union([z.string(), z.number()]);
export type CourierProfileId = z.infer<typeof courierProfileIdSchema>;

/**
 * Transport type the courier drives. Mirrors the 6-tile grid on the Profile tab
 * (Скутер / Велосипед / Автомобиль / Грузовая / Noor скутер / Свой скутер).
 * `.catch` keeps an unknown backend value from throwing — it collapses to
 * `scooter`, the default tile.
 */
export const transportTypeSchema = z
  .enum(['scooter', 'bike', 'car', 'truck', 'noor_scooter', 'own_scooter'])
  .catch('scooter');
export type TransportType = z.infer<typeof transportTypeSchema>;

/** A single removable tag chip on the Profile tab (e.g. Новичок, Гарант). */
export const courierTagSchema = z.object({
  id: courierProfileIdSchema,
  label: z.string(),
});
export type CourierTag = z.infer<typeof courierTagSchema>;

/**
 * A courier's profile header — the data behind the banner-overlap summary card
 * (avatar + name + subtitle + DescriptionList) and the Profile-tab personal
 * fields. Sums are integers in so'm; `rating` is 0–5 with a separate vote
 * `ratingCount`.
 */
export const courierProfileSchema = z.object({
  id: courierProfileIdSchema,
  fullName: z.string(),
  courierNumber: z.string(),
  avatarUrl: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  phone: z.string(),
  secondPhone: z.string().nullish(),
  birthDate: z.string().nullish(),
  documentType: z.string().nullish(),
  birthCountry: z.string().nullish(),
  citizenship: z.string().nullish(),
  nationality: z.string().nullish(),
  birthPlace: z.string().nullish(),
  pinfl: z.string().nullish(),
  issuedBy: z.string().nullish(),
  issuedDate: z.string().nullish(),
  expiryDate: z.string().nullish(),
  passportSeries: z.string().nullish(),
  status: z.string().nullish(),
  balance: z.number().nullish(),
  scooterBalance: z.number().nullish(),
  activeOrderId: courierProfileIdSchema.nullish(),
  appVersion: z.string().nullish(),
  os: z.string().nullish(),
  drivingLicenseFront: z.string().nullish(),
  drivingLicenseBack: z.string().nullish(),
  technicalPassportFront: z.string().nullish(),
  technicalPassportBack: z.string().nullish(),
  scooterDisplayId: z.string().nullish(),
  scooterModel: z.string().nullish(),
  scooterContractNumber: z.string().nullish(),
  scooterContractFile: z.string().nullish(),
  personalAnswers: z.object({
    married: z.boolean(),
    has_children: z.boolean(),
    has_prev_exp: z.boolean(),
    has_driving_exp: z.boolean(),
    order_issues: z.boolean(),
    considered_halal: z.boolean(),
    stress_issues: z.boolean(),
    bad_track_record: z.boolean(),
    has_issues_with_loans: z.boolean(),
    relationship_issues: z.boolean(),
    rating_comfirmation: z.boolean(),
    has_map_exp: z.boolean(),
  }),
  scooterBatteryId: courierProfileIdSchema.nullish(),
  scooterBatteryPercent: z.union([z.number(), z.string()]).nullish(),
  scooterBatteryLocation: z.object({ lat: z.number(), long: z.number() }).nullish(),
  batteryPercentage: z.union([z.number(), z.string()]).nullish(),
  batteryLocation: z.object({ lat: z.number(), long: z.number() }).nullish(),
  location: z.object({ lat: z.number(), long: z.number() }).nullish(),
  dealId: courierProfileIdSchema.nullish(),
  dealAccepted: z.boolean().nullish(),
  dealDate: z.string().nullish(),
  isOrderBlocked: z.boolean().nullish(),
  isIboxBlocked: z.boolean().nullish(),
  scooterId: courierProfileIdSchema.nullish(),
  equipmentId: courierProfileIdSchema.nullish(),
  chatId: courierProfileIdSchema.nullish(),
  requirements: z.object({
    passed_test: z.boolean(),
    visited_office: z.boolean(),
    commissioner_is_set: z.boolean(),
    driving_license_approved: z.boolean(),
    self_employment_status_set: z.boolean(),
  }),
  rating: z.number().min(0).max(5),
  ratingCount: z.number().int().nonnegative().nullable(),
  cashBalance: z.number(),
  earned: z.number().nullable(),
  transportType: transportTypeSchema,
  tags: z.array(courierTagSchema).default([]),
});
export type CourierProfile = z.infer<typeof courierProfileSchema>;

/** Backend flags behind the "Добавить личную информацию" Да/Нет questionnaire. */
export const COURIER_PERSONAL_ANSWER_FIELDS = [
  'married',
  'has_children',
  'has_prev_exp',
  'has_driving_exp',
  'order_issues',
  'considered_halal',
  'stress_issues',
  'bad_track_record',
  'has_issues_with_loans',
  'relationship_issues',
  'rating_comfirmation',
  'has_map_exp',
] as const;
export type CourierPersonalAnswerField = (typeof COURIER_PERSONAL_ANSWER_FIELDS)[number];
export type CourierPersonalAnswers = Record<CourierPersonalAnswerField, boolean>;

/** A scooter / battery row of the equipment pickers. */
export interface CourierPickerOption {
  id: CourierProfileId;
  label: string;
}
export type CourierRequirementField = keyof CourierProfile['requirements'];

export const courierBlockSchema = z
  .object({
    id: courierProfileIdSchema.nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    phone: z.string().nullish(),
    reason: z.string().nullish(),
    is_blocked: z.boolean().nullish(),
    blocked_at: z.string().nullish(),
  })
  .loose();
export type CourierBlock = z.infer<typeof courierBlockSchema>;

export const courierRatingSchema = z
  .object({
    id: courierProfileIdSchema.nullish(),
    order_id: courierProfileIdSchema.nullish(),
    order_display_id: courierProfileIdSchema.nullish(),
    rate: z.coerce.number().nullish(),
    custom_comment: z.string().nullish(),
    created_at: z.string().nullish(),
  })
  .loose();
export type CourierRating = z.infer<typeof courierRatingSchema>;

export const courierTestResultSchema = z
  .object({
    id: courierProfileIdSchema,
    name: z.string().nullish(),
    duration: z.coerce.number().default(0),
    correct_answers: z.coerce.number().default(0),
    total_questions: z.coerce.number().default(0),
    passed: z.boolean().default(false),
  })
  .loose();
export type CourierTestResult = z.infer<typeof courierTestResultSchema>;

/** Status of a courier order row on the Orders tab (bare colored text). */
export const courierOrderStatusSchema = z
  .enum(['active', 'cancelled', 'finished', 'returned', 're-offered'])
  .catch('finished');
export type CourierOrderStatus = z.infer<typeof courierOrderStatusSchema>;

/**
 * One row of the "Заказы курьера" table on the Orders tab: ID + created time,
 * the route (empty-run / order km chips + numbered addresses), status, delivery
 * price with its payment type, the client and the product price.
 */
export const courierOrderRowSchema = z.object({
  id: courierProfileIdSchema,
  displayId: z.string(),
  /** Origin addresses followed by destination addresses, in visiting order. */
  route: z.array(z.string()),
  deadHeadKm: z.number().nullable(),
  orderKm: z.number().nullable(),
  status: courierOrderStatusSchema,
  price: z.number().nullable(),
  /** Backend `payment_type` (`CASH` / `BALANCE` / card…), empty when unknown. */
  paymentType: z.string(),
  client: z.string(),
  productPrice: z.number().nullable(),
  time: z.string(),
  date: z.string(),
});
export type CourierOrderRow = z.infer<typeof courierOrderRowSchema>;

/**
 * The Contract tab payload: document/scooter form values + the boolean
 * requirement switches (Постоплата / Комиссия / Прямая оплата / Совместимость)
 * + the last saved-successfully marker driving the "✓ Успешно" history badge.
 */
export const courierContractRequirementsSchema = z.object({
  postpay: z.boolean().nullable(),
  commission: z.boolean().nullable(),
  directPay: z.boolean().nullable(),
  compatibility: z.boolean().nullable(),
});
export type CourierContractRequirements = z.infer<
  typeof courierContractRequirementsSchema
>;

export const courierContractSchema = z.object({
  documentNumber: z.string().nullish(),
  scooterModel: z.string().nullish(),
  scooterNumber: z.string().nullish(),
  photoUrl: z.string().nullish(),
  requirements: courierContractRequirementsSchema,
  lastSavedAt: z.string().nullish(),
  savedSuccessfully: z.boolean(),
});
export type CourierContract = z.infer<typeof courierContractSchema>;

/**
 * One equipment item the courier holds (the Equipment tab grid). `issued`
 * drives the selected-tile state; `size` is optional (helmet/jacket sizing).
 */
export const courierEquipmentItemSchema = z.object({
  id: courierProfileIdSchema,
  name: z.string(),
  issued: z.boolean(),
  size: z.string().nullish(),
});
export type CourierEquipmentItem = z.infer<typeof courierEquipmentItemSchema>;

export const courierEquipmentSchema = z.object({
  items: z.array(courierEquipmentItemSchema),
});
export type CourierEquipment = z.infer<typeof courierEquipmentSchema>;
